/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { SubEventsService } from 'src/app/shared/services/data-api/sub-events.service';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PortfolioNameComponent } from '../../news-portfolio/portfolio-name/portfolio-name.component';
import { ReportStateService } from 'src/app/shared/services/report-state.service';
import { NewsScoreStateService } from '../../news-score-state.service';

@Component({
  selector: 'app-chart-title',
  templateUrl: './chart-title.component.html',
  styleUrls: ['./chart-title.component.scss'],
})
export class ChartTitleComponent implements OnChanges {
  @Input() titolo: any;
  @Input() loading = false;

  @ViewChild('entityMenuTrigger') entityMenuTrigger: MatMenuTrigger | undefined;

  folio: any = null;
  entities: any[] = [];

  industryBenchmarkDescription = '';
  industryBenchmarkStatus = '';
  industryBenchmarkIndustry = '';

  isLoadingEvents = true;
  isLoadingBenchmark = false;

  industryBenchmarkScore: number | null = null;

  scoreEventsCount: number | null = null;
  scoreEventsTimeframe: number | null = null;
  scoreEventsDelta: number | null = null;

  params: any = {};

  private customScore: string;
  private previousValue: any;

  constructor(
    public routing: RoutingService,
    public auth: AuthService,
    public featureFlagsService: FeatureFlagsService,
    public config: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private subEvents: SubEventsService,
    private entityInfoService: EntityInfoService,
    private newsScoreState: NewsScoreStateService,
    public reportStateService: ReportStateService,
    private portfolioApi: PortfolioApiService,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
  ) {
    this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const timeframeChanged =
          this.previousValue && params.time !== this.previousValue.time;
        this.params = params;
        this.customScore =
          this.featureFlagsService.scorelabSettings.defaultScore;

        if (timeframeChanged && this.titolo) {
          this.scoreEventsCount = null;
          this.scoreEventsTimeframe = null;
          this.isLoadingEvents = true;
          this.getScoreEvents();
        }

        if (
          this.routing.reFresh(params, this.previousValue, [
            'id',
            'groupId',
            'portfolioId',
          ])
        ) {
          if (this.routing.isFolio()) {
            if (this.portfolioApi.portfolios) {
              const portfolioId = params.portfolioId
                ? params.portfolioId
                : params.id;
              this.folio = this.portfolioApi.portfolios.filter((el: any) => {
                return el.uid === portfolioId;
              })[0];
              const folioEntities = this.folio?.content;
              if (folioEntities) {
                this.entityInfoService
                  .getEntitiesInfo(folioEntities)
                  .subscribe((result: any) => {
                    this.entities = Object.values(result).filter((element) => {
                      return element !== null;
                    });
                    this.entities.forEach((item: any) => {
                      item.name =
                        item.name !== 'PNE'
                          ? item.name
                          : item.alpha_id.split('.')[1] +
                            ' (' +
                            item.alpha_id.split('.')[0] +
                            ')';
                    });
                  });
              }
            } else {
              this.entities = [];
            }
          }
        }

        this.previousValue = params;
      });
  }

  get isNarrow(): boolean {
    return window.innerWidth < 1550;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['titolo']) {
      const previousValue = changes['titolo'].previousValue;
      const currentValue = changes['titolo'].currentValue;

      if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
        this.executeOnChanges();
      }
    }
  }

  executeOnChanges(): void {
    if (this.titolo) {
      this.reportStateService.checkStatuses({
        mode: 'entity',
        entity: this.titolo.entity,
        folio: this.folio,
        customScore: this.featureFlagsService.scorelabSettings.defaultScore,
      });
      this.scoreEventsCount = null;
      this.scoreEventsTimeframe = null;
      this.industryBenchmarkScore = null;
      this.isLoadingEvents = true;
      this.getIndustryBenchmark();
      this.getScoreEvents();
    }
  }

  // ── Industry benchmark ────────────────────────────────────────────────────

  private getIndustryBenchmark(): void {
    const benchScores = [
      'esg',
      'eb_sim',
      'eb_sim_risk',
      'general_risk',
      'esg_risk',
      'immediate_risk',
      'credit_risk',
      'bb',
    ];
    if (
      benchScores.includes(this.customScore) &&
      this.params?.type === 'company'
    ) {
      this.isLoadingBenchmark = true;
      this.newsScoreState
        .fetchIndustryBenchmark(this.customScore, this.params)
        .pipe(catchError(() => of({ score_ts_last_score: { score: null } })))
        .subscribe((res: any) => {
          const industry =
            this.titolo?.entity?.description?.Industry?.name ?? 'Industry';
          const s = res?.score_ts_last_score?.score ?? null;
          const labels: Record<number, string> = {
            0: 'Sector laggard',
            1: 'Below average',
            2: 'Above average',
            3: 'Sector leader',
          };
          const hasScore = s !== null && labels[s] !== undefined;
          this.industryBenchmarkScore = hasScore ? s : null;
          this.industryBenchmarkStatus = hasScore ? labels[s] : '';
          this.industryBenchmarkIndustry = hasScore ? industry : '';
          this.industryBenchmarkDescription = hasScore
            ? `${labels[s]} • ${industry}`
            : '';
          this.isLoadingBenchmark = false;
        });
    } else {
      this.industryBenchmarkDescription = '';
      this.isLoadingBenchmark = false;
    }
  }

  // ── Score events ──────────────────────────────────────────────────────────

  private getScoreEvents(): void {
    const shouldFetch =
      this.customScore === 'sentiment' ||
      this.routing.isFolio() ||
      (this.params.type &&
        (this.params.type === 'company' ||
          this.params.type === 'pne' ||
          ['industry', 'supersector', 'sector', 'subsector'].includes(
            this.params.type,
          ))) ||
      this.params.isin;

    if (!shouldFetch) {
      this.isLoadingEvents = false;
      return;
    }

    this.isLoadingEvents = true;
    this.scoreEventsDelta = null;

    // The events count is the essential value — render it (and clear loading) as
    // soon as it arrives. The previous-period delta is a best-effort pill fetched
    // separately, so a stalled/empty delta request can never hang the count.
    this.subEvents
      .fetchSubEventsTotal(this.params)
      .pipe(catchError(() => of(0)))
      .subscribe((current) => {
        this.scoreEventsCount = current;
        this.scoreEventsTimeframe = this.params.time
          ? DateUtils.fromTo(this.params.time).period
          : null;
        this.isLoadingEvents = false;
        this.fetchScoreEventsDelta(current);
      });
  }

  // Best-effort previous-period delta for the events pill. Independent of the
  // loading state — failure just leaves the delta null.
  private fetchScoreEventsDelta(current: number): void {
    const previousTime = this.computePreviousPeriodTime();
    if (!previousTime) return;
    this.subEvents
      .fetchSubEventsTotal(this.params, previousTime)
      .pipe(catchError(() => of(null)))
      .subscribe((previous) => {
        this.scoreEventsDelta = previous !== null ? current - previous : null;
      });
  }

  // Build a `date1--date2` string for the period immediately preceding the
  // user's current global-filter window. Used to fetch previous-period event
  // counts for the delta pill.
  private computePreviousPeriodTime(): string | null {
    if (!this.params.time) return null;
    const fromto = DateUtils.fromTo(this.params.time);
    if (!fromto?.dateFrom || !fromto?.period) return null;
    const startDate = new Date(fromto.dateFrom);
    if (isNaN(startDate.getTime())) return null;
    const prevStart = new Date(startDate);
    prevStart.setDate(prevStart.getDate() - fromto.period);
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    return `${fmt(prevStart)}--${fmt(startDate)}`;
  }

  // ── Score display ─────────────────────────────────────────────────────────

  // The score currently on the chart: a focused score (alert/selection) when
  // present, otherwise the user's default. Drives the title's score label/pill.
  private get activeScore(): string {
    return this.params?.score || this.customScore;
  }

  get scoreName(): string {
    if (this.isSentiment) return 'Sentiment';
    if (this.isBb) return 'Bull/Bear';
    return (
      (this.auth.getScoresInfo(this.activeScore) as any)?.['name'] ??
      'Sentiment'
    );
  }

  get isBb(): boolean {
    return this.activeScore === 'bb';
  }

  get sentimentDelta(): number {
    return (this.titolo?.sentiment?.sentiment_delta ?? 0) * 100;
  }

  // Selected-score delta: last - first non-null score, set by the parent
  // news-chart component on the titolo payload. Null hides the pill.
  get scoreDelta(): number | null {
    const raw = this.titolo?.score_delta;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  }

  // Color the pill against the score's polarity: for risk scores (non-'pos')
  // a rise is bad, for positive scores a rise is good.
  get scoreDeltaIsPositive(): boolean {
    if (this.scoreDelta === null || this.scoreDelta === 0) return false;
    return this.auth.getScoreType(this.activeScore) === 'pos'
      ? this.scoreDelta > 0
      : this.scoreDelta < 0;
  }

  get scoreDeltaIsNegative(): boolean {
    if (this.scoreDelta === null || this.scoreDelta === 0) return false;
    return this.auth.getScoreType(this.activeScore) === 'pos'
      ? this.scoreDelta < 0
      : this.scoreDelta > 0;
  }

  // Display value for the score-delta pill. Shows one decimal unless the decimal
  // is 0 (then displays the integer); falls back to "0.1" when the raw delta is
  // non-zero but rounds to 0.0.
  get displayScoreDelta(): string {
    if (this.scoreDelta === null || this.scoreDelta === 0) return '';
    const display = Math.abs(this.scoreDelta).toFixed(1);
    if (display === '0.0') return '0.1';
    return display.endsWith('.0') ? display.slice(0, -2) : display;
  }

  // Display value for the events-delta pill (vs previous period, neutral pill).
  get displayEventsDelta(): number {
    return Math.abs(this.scoreEventsDelta ?? 0);
  }

  get isSentiment(): boolean {
    return (
      !Object.prototype.hasOwnProperty.call(this.titolo, 'score') ||
      this.titolo?.score === null
    );
  }

  // ── Save to portfolio (market mode) ──────────────────────────────────────

  get isEntityInAnyPortfolio(): boolean {
    if (!this.portfolioApi.portfolios || !this.params.id) return false;
    return this.portfolioApi.portfolios.some((folio: any) =>
      folio.content?.includes(this.params.id),
    );
  }

  onSave(action: string): void {
    this.dialog.open(PortfolioNameComponent, {
      data: { action, entity: this.titolo?.entity },
    });
  }

  // ── Entity selection dropdown (folio mode) ────────────────────────────────

  openEntityMenu(): void {
    this.entityMenuTrigger?.openMenu();
  }

  select(item: any): void {
    if (item === 'overall') {
      this.router.navigate([], {
        queryParams: {
          id: this.params.portfolioId
            ? this.params.portfolioId
            : this.params.id,
          type: null,
          newstype:
            this.params.newstype === 'Events'
              ? 'Chronological'
              : this.params.newstype,
          portfolioId: null,
          eventid: null,
          score: null,
          time: this.params.score ? null : this.params.time,
          reflow: Math.random(),
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate([], {
        queryParams: {
          id: item.alpha_id,
          type: item.type,
          newstype: this.params.newstype,
          eventid: null,
          portfolioId: this.params.portfolioId
            ? this.params.portfolioId
            : this.params.id,
          score: null,
          time: this.params.time,
          reflow: Math.random(),
          focused_score: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }
}
